import { Adjust } from "@awesome-cordova-plugins/adjust";
import { Box, makeStyles } from "@material-ui/core";
import { useSnackbar } from "notistack";
import * as React from "react";
import { useHistory } from "react-router";

import { CustomButton } from "@/components/Button";
import { Loading } from "@/components/Loading";
import { PublicHeader } from "@/components/PublicHeader";
import { TextLink } from "@/components/TextLink";
import { useDevice } from "@/hooks/useDevice";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import theme from "@/theme";
import { isNativeApp } from "@/utilities/capacitorLogic";
import { AppEventTokenEnum, sendEventToAppMeasurementTool } from "@/utilities/capacitorLogic";
import { markExpoRegistration } from "@/utilities/expoRegistration";
import { peekIntent } from "@/utilities/intentNavigator";

import CityBackground from "@assets/image/registration_v2/city.png";
import OnboardingLogo from "@assets/image/registration_v2/logo.svg";
import OnboardingTagline from "@assets/image/registration_v2/tagline.svg";

import { OnboardingCarousel } from "./components/OnboardingCarousel";
import { useHiddenTapTrigger } from "./hooks/useHiddenTapTrigger";

// エキスポ会場用の登録導線（Trello #241）に入る隠し操作。ロゴを短時間に連続タップしたときだけ発動し、
// 一般ユーザーには何も表示しない。回数・時間はスタッフに口頭で伝える前提の固定値。
// 発動したことをスタッフが確かめられるよう、通知を出してから登録導線へ進む
const EXPO_TRIGGER_TAP_COUNT = 7;
const EXPO_TRIGGER_WINDOW_MS = 3000;
// 発動通知は直後に画面遷移するため、全体設定（app.tsx）の 2 秒ではスタッフが読み切れない。エキスポ向けの 2 通知だけ表示時間を伸ばす
const EXPO_MESSAGE_AUTO_HIDE_MS = 4000;
export const EXPO_START_MESSAGE = "エキスポ用の登録を開始します";
export const EXPO_UNAVAILABLE_MESSAGE =
  "この端末ではエキスポ用の登録を開始できません。通常の登録は「プロパリーをはじめる」から進めてください";

const useStyles = makeStyles(() => ({
  root: {
    minHeight: "100vh",
    backgroundColor: theme.palette.semantic["surface/card"],
    touchAction: "pan-y",
    [theme.breakpoints.up("md")]: { backgroundColor: theme.palette.semantic["surface/base"] },
  },
  main: {
    position: "relative",
    minHeight: "100vh",
    padding: "calc(48px + env(safe-area-inset-top, 0px)) 24px calc(32px + env(safe-area-inset-bottom, 0px))",
    overflow: "hidden",
    textAlign: "center",
    [theme.breakpoints.up("md")]: {
      width: 560,
      margin: "48px auto 64px",
      padding: "40px 48px",
      borderRadius: 16,
      minHeight: 0,
      backgroundColor: theme.palette.semantic["surface/card"],
    },
  },
  city: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    opacity: 0.25,
    pointerEvents: "none",
    [theme.breakpoints.up("md")]: { display: "none" },
  },
  content: { position: "relative", display: "flex", flexDirection: "column", alignItems: "center" },
  logo: {
    height: 39,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 32,
  },
  actions: { width: "100%", marginTop: 32, maxWidth: 360, "& button": { maxWidth: "100%" } },
  login: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginTop: 16,
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
}));

export const OnboardingPage: React.FC = () => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [checkedSession, setCheckedSession] = React.useState(false);
  const pendingRef = React.useRef<string | null>(peekIntent());

  const onClickRegister = React.useCallback(() => {
    history.push("/registration_intro");
  }, []);

  const onClickLogin = React.useCallback(() => {
    history.push("/login");
  }, []);

  const onTriggerExpo = React.useCallback(() => {
    // 目印を端末に残せない（= 途中でスタイル診断へ戻される）なら、導線に入れずに案内だけ出す
    if (!markExpoRegistration()) {
      enqueueSnackbar(EXPO_UNAVAILABLE_MESSAGE, { variant: "error", autoHideDuration: EXPO_MESSAGE_AUTO_HIDE_MS });
      return;
    }
    enqueueSnackbar(EXPO_START_MESSAGE, { autoHideDuration: EXPO_MESSAGE_AUTO_HIDE_MS });
    history.push("/registration_intro");
  }, [enqueueSnackbar, history]);
  const onTapLogo = useHiddenTapTrigger(EXPO_TRIGGER_TAP_COUNT, EXPO_TRIGGER_WINDOW_MS, onTriggerExpo);

  React.useEffect(() => {
    (async () => {
      try {
        // Adjust Attribution から network を取得（取得できた場合のみ返却）
        // 検証環境ではAdjustを初期化しないため、ネイティブアプリにて適切に値が返らない。
        // またフェイルセーフとしてタイムアウトを設けてAPI呼び出しをブロックしないようにする
        const getAdjustNetwork = async (): Promise<string | undefined> => {
          if (!isNativeApp) return undefined;
          const fetch = async () => {
            try {
              const hasGetter = typeof (Adjust as any)?.getAttribution === "function";
              if (!hasGetter) return undefined;
              const attribution = await Adjust.getAttribution();
              const v = attribution?.network?.trim();
              return v || undefined;
            } catch (_) {
              return undefined;
            }
          };
          const timeoutMs = 800;
          const timeout = new Promise<string | undefined>((resolve) => setTimeout(() => resolve(undefined), timeoutMs));
          return Promise.race([fetch(), timeout]);
        };
        const adjust_network = await getAdjustNetwork();
        await PropallyApiFactory().v1SessionGet(adjust_network, { withCredentials: true });
        // セッションあり → 通知の行き先があれば最優先
        const dest = pendingRef.current ?? "/mypage";
        history.replace(dest);
      } catch (e: any) {
        if (e.response?.status === 401) {
          // 未ログイン
          if (pendingRef.current) {
            // 未ログインで通知をタップした場合は、ログイン後に画面遷移させる
            history.replace("/login");
          } else {
            // 意図通りTOP表示 → 計測イベント
            sendEventToAppMeasurementTool(AppEventTokenEnum.RegistrationTopAccess);
          }
        } else {
          console.log("API接続失敗:", e);
        }
      } finally {
        setCheckedSession(true);
      }
    })();
  }, []);

  if (!checkedSession) return <Loading />;

  return (
    <Box className={classes.root}>
      {!isMobile && <PublicHeader />}
      <Box component="main" className={classes.main}>
        <img src={CityBackground} alt="" className={classes.city} />
        <Box className={classes.content}>
          <Box className={classes.logo}>
            <img src={OnboardingTagline} alt="不動産投資なら" />
            <img src={OnboardingLogo} alt="Propally" onClick={onTapLogo} />
          </Box>
          <OnboardingCarousel />
          <Box className={classes.actions}>
            <CustomButton buttonSize="L" type="button" tone="primary" onClick={onClickRegister}>
              プロパリーをはじめる
            </CustomButton>
          </Box>
          <Box className={classes.login}>
            <span>アカウントをお持ちの方は</span>
            <TextLink href="/login" onClick={onClickLogin}>
              ログイン
            </TextLink>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
